import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

import { nodeExecutionsApi } from "src/api/endpoints";
import { decisionHistoryV3ApiRoute } from "src/api/exporterApi";
import { GenericObject } from "src/api/flowTypes";
import { ndjsonRowToNodeExecutionResult } from "src/api/testRuns/utils";
import { NodeExecutionMetadata } from "src/api/types";
import { DecisionEnvironment as DecisionEnvironmentV3 } from "src/clients/history_v3";

export type NodeExecutionStatusFilter = "successful" | "failed" | "ignored";

type GetNodeExecutionsParams = {
  baseUrl: string;
  nodeId: string;
  jobRunId: string;
  flowId: string;
  environment: DecisionEnvironmentV3;
  fromStartTime: string;
  untilStartTime?: string;
  pageSize: number;
  enabled: boolean;
  statuses?: NodeExecutionStatusFilter[];
  shouldPoll: boolean;
};

export type NodeExecutionResult = {
  data: GenericObject;
  decision_metadata: {
    id: string;
    status_code: string;
    error: string | null;
    source_entry_id: string | null;
  };
  execution_metadata: NodeExecutionMetadata;
  tracing_data: Record<string, GenericObject>;
  node_id: string;
  node_name: string;
};

type NodeExecutionsPage = {
  query_params: object;
  results: NodeExecutionResult[];
};

export const NODE_EXECUTIONS_POLLING_INTERVAL = 3000;

export const buildNodeExecutionsQueryKey = (
  args: GetNodeExecutionsParams,
): QueryKey => {
  return [
    "nodeExecutions",
    args.baseUrl,
    args.nodeId,
    args.jobRunId,
    args.flowId,
    args.environment,
    args.fromStartTime,
    args.pageSize,
    args.statuses,
  ];
};

export const useGetNodeExecutions = (args: GetNodeExecutionsParams) => {
  const queryKey = buildNodeExecutionsQueryKey(args);

  const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery<
      NodeExecutionsPage,
      Error,
      InfiniteData<NodeExecutionsPage>,
      QueryKey,
      string | null
    >({
      queryKey,
      queryFn: async ({ pageParam }) => {
        const response =
          await decisionHistoryV3ApiRoute.get<NodeExecutionsPage>(
            `/node_executions/${args.nodeId}`,
            {
              params: {
                job_run_id: args.jobRunId,
                flow_id: args.flowId,
                environment: args.environment,
                from_start_time: args.fromStartTime,
                until_start_time: args.untilStartTime,
                page_size: args.pageSize,
                statuses: args.statuses,
                ...(pageParam && { after_decision_id: pageParam }),
              },
              baseURL: args.baseUrl,
            },
          );
        return response.data;
      },
      getNextPageParam: (lastPage, _pages, lastPageParam) => {
        const newPageParam =
          lastPage.results.at(-1)?.decision_metadata.id ?? null;
        return lastPageParam === newPageParam ? null : newPageParam;
      },
      initialPageParam: null,
      refetchInterval: () =>
        args.shouldPoll ? NODE_EXECUTIONS_POLLING_INTERVAL : false,
      staleTime: Infinity,
      enabled: args.enabled,
    });

  return {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isPolling: args.shouldPoll,
    fetchNextPage,
  };
};

type GetEagerNodeExecutionsParams = {
  baseUrl: string;
  nodeId: string;
  jobRunId: string;
  enabled: boolean;
};

const fetchNodeExecutionsShard = async (
  url: string,
  nodeId: string,
): Promise<NodeExecutionResult[]> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Error fetching node results shard: ${
        response.status
      } ${await response.text()}`,
    );
  }
  const text = await response.text();
  return text
    .split("\n")
    .filter((line) => line.trim().length > 0)
    .map((line) =>
      ndjsonRowToNodeExecutionResult(
        JSON.parse(line) as Record<string, unknown>,
        nodeId,
      ),
    );
};

// Eagerly-generated variant of useGetNodeExecutions for completed runs: loads
// the pre-materialised per-node result files from S3 instead of querying live.
export const useGetEagerNodeExecutions = (
  args: GetEagerNodeExecutionsParams,
) => {
  // The files exist only once the backend writes the manifest. Poll until it
  // reports available, then stop — for a completed run it doesn't flip back.
  const manifestQuery = useQuery<boolean>({
    queryKey: ["nodeExecutionsManifest", args.baseUrl, args.jobRunId],
    queryFn: async () => {
      const { data } = await nodeExecutionsApi(
        args.baseUrl,
      ).getNodeExecutionsManifestHistoryApiV3JobRunsJobRunIdNodeExecutionsManifestGet(
        { jobRunId: args.jobRunId },
      );
      return data.available;
    },
    enabled: true,
    staleTime: Infinity,
    refetchInterval: (query) =>
      query.state.data === true ? false : NODE_EXECUTIONS_POLLING_INTERVAL,
  });

  const available = manifestQuery.data ?? false;

  // Keyed by job run (not node) so every node shares one cached request.
  const urlsQuery = useQuery<Record<string, string[]>>({
    queryKey: ["nodeExecutionsFileUrls", args.baseUrl, args.jobRunId],
    queryFn: async () => {
      const { data } = await nodeExecutionsApi(
        args.baseUrl,
      ).getNodeExecutionFileUrlsHistoryApiV3JobRunsJobRunIdNodeExecutionsGet({
        jobRunId: args.jobRunId,
      });
      return data.urls_by_node_id;
    },
    staleTime: Infinity,
    enabled: args.enabled && available,
  });

  const urls = urlsQuery.data?.[args.nodeId] ?? [];

  // Load all shards up front and sort by decision_id (UUIDv7 = processing order)
  // to match the row order the live node_executions endpoint returns.
  const dataQuery = useQuery<InfiniteData<NodeExecutionsPage>>({
    queryKey: [
      "nodeExecutionsFromFiles",
      args.baseUrl,
      args.nodeId,
      args.jobRunId,
    ],
    queryFn: async () => {
      const shards = await Promise.all(
        urls.map((url) => fetchNodeExecutionsShard(url, args.nodeId)),
      );
      const results = shards
        .flat()
        .sort((a, b) =>
          a.decision_metadata.id.localeCompare(b.decision_metadata.id),
        );
      return { pages: [{ query_params: {}, results }], pageParams: [0] };
    },
    enabled: args.enabled && available && urls.length > 0,
    staleTime: Infinity,
  });

  return {
    data: dataQuery.data,
    isFetching:
      dataQuery.isFetching || urlsQuery.isFetching || manifestQuery.isFetching,
    isFetchingNextPage: false,
    hasNextPage: false,
    isPolling: false,
    isManifestAvailable: manifestQuery.data === true,
    fetchNextPage: () => {},
    // Excludes a failed shard load so the wrapper falls back to live instead of
    // showing an empty table.
    hasNodeResults: available && urls.length > 0 && !dataQuery.isError,
    // True once the outcome is settled, so the wrapper can fall back to live
    // without double-fetching while files are still loading.
    urlsResolved:
      manifestQuery.isFetched &&
      (!available || urlsQuery.isFetched) &&
      !dataQuery.isFetching,
  };
};
